import { useMemo } from 'react';
import { useAskSalahSubscription } from './useAskSalahSubscription';

/**
 * Hook to check if user is eligible for Ask Salah paid plans (Basic/Premium)
 * 
 * Eligibility Rules:
 * - Requires PAID SAL125 subscription (not free trial)
 * - Allows upgrades: trial → paid, basic → premium
 * - Individual cards will show "Current Plan" and disable if already subscribed
 * 
 * Returns:
 * - eligible: Boolean
 * - reason: String explanation if not eligible
 * - message: User-friendly message
 */
export const useAskSalahPaidPlanEligibility = () => {
  const {
    hasSAL125Subscription,
    canAccessPaidPlans,
    hasAskSalahAccess,
    planType: currentPlanType,
  } = useAskSalahSubscription();

  const result = useMemo(() => {
    // Check if user has SAL125 subscription
    if (!hasSAL125Subscription) {
      return {
        eligible: false,
        reason: 'no_sal125_subscription' as const,
        message: 'Please subscribe to SAL125 to access Ask Salah paid plans.',
      };
    }

    // Check if SAL125 subscription is paid (not free trial)
    if (!canAccessPaidPlans) {
      return {
        eligible: false,
        reason: 'sal125_free_trial_only' as const,
        message: 'Please upgrade your SAL125 subscription to access Ask Salah paid plans.',
      };
    }

    // REMOVED: No longer blocking users with existing subscriptions
    // Allow users to upgrade from trial → paid or basic → premium
    // The individual card will handle disabling if they try to select their current plan

    return {
      eligible: true,
      reason: null,
      message: 'You are eligible for Ask Salah paid plans!',
    };
  }, [hasSAL125Subscription, canAccessPaidPlans, hasAskSalahAccess, currentPlanType]);

  return result;
};


